import { DebitCardColour, VelocityWindowEnum } from 'types/gql-global-types'

export enum AddCardStage {
  LandingPageStage = 'LandingPageStage',

  LinkCardIdStage = 'LinkCardIdStage',
  LinkEMLCardInfoStage = 'LinkEMLCardInfoStage',
  LinkSelectAccountStage = 'LinkSelectAccountStage',

  CreateSelectCardTypeStage = 'CreateSelectCardTypeStage',
  CreateSelectCardholderStage = 'CreateSelectCardholderStage',
  CreateSelectAccountStage = 'CreateSelectAccountStage',
  CreateSelectCardFormatStage = 'CreateSelectCardFormatStage',
  CreateSetCardSettingsStage = 'CreateSetCardSettingsStage',
  CreateSpendControlsStage = 'CreateSpendControlsStage',
  CreateSelectCardDesignStage = 'CreateSelectCardDesignStage',
  CreateSelectDeliveryAddressStage = 'CreateSelectDeliveryAddressStage',
  CreateReviewCardStage = 'CreateReviewCardStage',
}

interface LandingPageState {
  stage: AddCardStage.LandingPageStage
}

export enum LinkCardError {
  NotCardholderError = 'NotCardholderError',
  ManagerBlankCardError = 'ManagerBlankCardError',
  CardIDNotFoundError = 'CardIDNotFoundError',
  CardNumberLengthError = 'CardNumberLengthError',
}

export interface LinkCardIdState {
  stage: AddCardStage.LinkCardIdStage
  linkCardError?: LinkCardError
}

interface LinkEMLCardInfoStage {
  stage: AddCardStage.LinkEMLCardInfoStage
}

export interface LinkSelectAccountState {
  stage: AddCardStage.LinkSelectAccountStage
  cardId: string
  accountId?: string
}

type LinkCardState =
  | LinkCardIdState
  | LinkSelectAccountState
  | LinkEMLCardInfoStage

export enum CardTypeOption {
  DebitCard = 'DebitCard',
  ExpenseCard = 'ExpenseCard',
}

export interface CreateSelectCardTypeState {
  stage: AddCardStage.CreateSelectCardTypeStage
  cardType?: CardTypeOption
}

interface Cardholder {
  id: string
  firstName: string
  lastName: string
}

interface Account {
  id: string
  name: string
}

export interface VelocityControl {
  amountLimit: string
  maxTransactionValue: string
  velocityWindow: VelocityWindowEnum
}

export interface CreateSelectCardholderState {
  stage: AddCardStage.CreateSelectCardholderStage
  cardType: CardTypeOption
  cardholder?: Cardholder
}

export interface CreateSelectAccountState {
  stage: AddCardStage.CreateSelectAccountStage
  cardType: CardTypeOption
  cardholder: Cardholder
  account?: Account
}

export enum CardFormatOption {
  Physical = 'Physical',
  Virtual = 'Virtual',
}

export interface CreateSelectCardFormatState {
  stage: AddCardStage.CreateSelectCardFormatStage
  cardType: CardTypeOption
  cardholder: Cardholder
  account: Account
  cardFormat?: CardFormatOption
}

export interface CreateSetCardSettingsState {
  stage: AddCardStage.CreateSetCardSettingsStage
  cardType: CardTypeOption
  cardholder: Cardholder
  account: Account
  cardFormat: CardFormatOption
  nickname?: string
  usePin?: boolean
}

export interface CreateSpendControlsState {
  stage: AddCardStage.CreateSpendControlsStage
  cardType: CardTypeOption.ExpenseCard
  cardholder: Cardholder
  account: Account
  cardFormat: CardFormatOption
  nickname: string
  usePin: boolean
  velocityControl?: VelocityControl
}

export interface CreateSelectCardDesignState {
  stage: AddCardStage.CreateSelectCardDesignStage
  cardType: CardTypeOption
  cardholder: Cardholder
  account: Account
  cardFormat: CardFormatOption
  nickname: string
  usePin: boolean
  velocityControl?: VelocityControl
  colour?: DebitCardColour
  entityAddressTimezone?: string
}

export enum AddressPage {
  PredefinedAddress = 'PredefinedAddress',
  CustomAddress = 'CustomAddress',
}

export interface Address {
  street1: string | null
  street2: string | null
  suburb: string | null
  state: string | null
  postcode: string | null
  country: string | null
}

export interface CreateSelectDeliveryAddressState {
  stage: AddCardStage.CreateSelectDeliveryAddressStage
  cardType: CardTypeOption
  cardholder: Cardholder
  account: Account
  cardFormat: CardFormatOption
  nickname: string
  usePin: boolean
  velocityControl?: VelocityControl
  colour: DebitCardColour
  addressPage?: AddressPage
  address?: Address
  entityAddressTimezone?: string
}

export interface CreateReviewCardState {
  stage: AddCardStage.CreateReviewCardStage
  cardType: CardTypeOption
  cardholder: Cardholder
  account: Account
  cardFormat: CardFormatOption
  nickname: string
  usePin: boolean
  velocityControl?: VelocityControl
  colour: DebitCardColour
  address?: Address
  entityAddressTimezone?: string
}

type CreateCardState =
  | CreateSelectCardTypeState
  | CreateSelectCardholderState
  | CreateSelectAccountState
  | CreateSelectCardFormatState
  | CreateSpendControlsState
  | CreateSetCardSettingsState
  | CreateSelectCardDesignState
  | CreateSelectDeliveryAddressState
  | CreateReviewCardState

export type AddCardState = LandingPageState | LinkCardState | CreateCardState
